import React, { useMemo, useState } from "react";
import classNames from "classnames";
import styles from "./TreeSelector.module.scss";
import {ReactComponent as ExpandIcon} from "../TagsHierarchyChooser/assets/chevron.svg";
import { LoaderSmall } from "../LoaderSmall/LoaderSmall";
import { useTranslation } from "react-i18next";

type TreeSelectorProps<T> = {
  items: T[] | null;
  selectedIds: (keyof T)[];
  onChange: (selected: (keyof T)[]) => void;
  groupBy: (item: T) => string;
  idKey: keyof T;
  labelKey: keyof T;
  multiple?: boolean;
  disabled?: boolean;
  style?: React.CSSProperties;
  /** Visual style variant. `'legacy'` (default) uses the compact sidebar-filter style;
   *  `'modern'` uses the spacious panel/dropdown style. */
  mode?: 'legacy' | 'modern';
};

export const TreeSelector = <T extends Record<string, any>>({
  items,
  selectedIds,
  onChange,
  groupBy,
  idKey,
  labelKey,
  multiple = true,
  disabled = false,
  style={},
  mode = 'legacy',
}: TreeSelectorProps<T>) => {
  const { t } = useTranslation();
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  const grouped = useMemo(() => {
    if (!items) {
      return {}
    };
    return items.reduce((acc, item) => {
      const group = groupBy(item);
      if (!acc[group]) {
        acc[group] = []
      };
      acc[group].push(item);
      return acc;
    }, {} as Record<string, T[]>);
  }, [items, groupBy]);

  const toggleGroup = (group: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      next.has(group) ? next.delete(group) : next.add(group);
      return next;
    });
  };

  const handleItemToggle = (id: keyof T) => {
    let next: (keyof T)[];
    if (multiple) {
      next = selectedIds.includes(id)
        ? selectedIds.filter((sid) => sid !== id)
        : [...selectedIds, id];
    } else {
      next = [id];
    }
    onChange(next);
  };

  const handleGroupToggle = (group: string) => {
    const groupItems = grouped[group];
    const ids = groupItems.map((item) => item[idKey]);
    const allSelected = ids.every((id) => selectedIds.includes(id));

    const next = allSelected
      ? selectedIds.filter((id) => !ids.includes(id as T[keyof T]))
      : multiple
      ? [...selectedIds, ...ids.filter((id) => !selectedIds.includes(id))]
      : [ids[0]];

    onChange(next);
  };

  const isIndeterminate = (group: string) => {
    const groupIds = grouped[group].map((i) => i[idKey]);
    const selectedCount = groupIds.filter((id) => selectedIds.includes(id)).length;
    return selectedCount > 0 && selectedCount < groupIds.length;
  };

  const isGroupChecked = (group: string) => {
    const groupIds = grouped[group].map((i) => i[idKey]);
    return groupIds.every((id) => selectedIds.includes(id));
  };

  if (!items) return <div className={styles.loading}>{<LoaderSmall/>}</div>;

  return (
    <div
      className={classNames(
        styles.treeRoot,
        disabled && styles.disabled,
        mode === 'modern' && styles.modernMode,
      )}
      style={style}
    >
      {Object.entries(grouped).map(([group, groupItems]) => {
        const expanded = expandedGroups.has(group);
        const isChecked = isGroupChecked(group);

        return (
          <div key={group} className={styles.treeNode}>
            <div className={classNames(styles.treeHeader, isChecked && styles.checked)}>
              <button
                type="button"
                className={classNames(styles.expandIcon, expanded && styles.expanded)}
                onClick={() => toggleGroup(group)}
                disabled={disabled}
                aria-label={expanded ? "Collapse" : "Expand"}
              >
                <ExpandIcon
                  aria-hidden="true"
                />
              </button>

              {multiple ? (
                <label className={styles.treeLabel}>
                  {mode === 'modern' ? (
                    <span className={styles.checkboxWrapper}>
                      <input
                        type="checkbox"
                        disabled={disabled}
                        checked={isChecked}
                        ref={(el) => {
                          if (el) el.indeterminate = isIndeterminate(group);
                        }}
                        onChange={() => handleGroupToggle(group)}
                        className={styles.checkboxInput}
                      />
                      <span className={styles.checkboxCustom} aria-hidden="true" />
                    </span>
                  ) : (
                    <input
                      type="checkbox"
                      disabled={disabled}
                      checked={isChecked}
                      ref={(el) => {
                        if (el) el.indeterminate = isIndeterminate(group);
                      }}
                      onChange={() => handleGroupToggle(group)}
                    />
                  )}
                  <span className={styles.treeTitle}>{t(group)}</span>
                </label>
              ) : (
                <span className={styles.treeTitle}>{t(group)}</span>
              )}
            </div>

            {expanded && (
              <div className={styles.treeChildren}>
                {groupItems.map((item) => {
                  const id = item[idKey];
                  const label = item[labelKey];
                  return (
                    <label key={`${String(id)}_tree_label`} className={styles.treeLabel}>
                      {multiple && mode === 'modern' ? (
                        <span className={styles.checkboxWrapper}>
                          <input
                            type="checkbox"
                            name={`tree-selector-${group}`}
                            disabled={disabled}
                            checked={selectedIds.includes(id)}
                            onChange={() => handleItemToggle(id)}
                            className={styles.checkboxInput}
                          />
                          <span className={styles.checkboxCustom} aria-hidden="true" />
                        </span>
                      ) : (
                        <input
                          type={multiple ? "checkbox" : "radio"}
                          name={`tree-selector-${group}`}
                          disabled={disabled}
                          checked={selectedIds.includes(id)}
                          onChange={() => handleItemToggle(id)}
                        />
                      )}
                      <span>{t(label)}</span>
                    </label>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};
